import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { useConvexAuth, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';

const SHARED_COORDINATOR_URL = (import.meta.env.VITE_SHARED_COORDINATOR_URL || 'https://next-events-engine.will-026.workers.dev').replace(/\/*$/, '');

type PairingStartData = {
  pairingCode: string;
  accountId: string;
  expiresAt: number | string;
};

type PairingStatusData = {
  status: 'pending' | 'claimed' | 'expired' | 'revoked';
  organizationId: string;
  requestedClientType?: string;
  requestedAppProduct?: string;
  deviceNameHint?: string;
  targetLibraryId?: string;
  libraryNameHint?: string;
  localLibraryUuidHint?: string;
  membershipRole?: string;
  trustState?: string;
  createdAt?: string;
  expiresAt?: string;
  claimedDeviceId?: string;
};

type ApiResponse<T> = {
  ok: boolean;
  data?: T;
  error?: string;
};

function normalizeTrimmed(value: string) {
  return value.trim();
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function PairPage() {
  const convexAuth = useConvexAuth();
  const { signIn } = useAuth();
  const user = useQuery(api.users.getCurrentUser, {});
  const orgs = useQuery(api.organizations.listForUser, {});
  const orgOptions = useMemo(() => orgs ?? [], [orgs]);

  const [appProduct, setAppProduct] = useState('event_tools');
  const [clientType, setClientType] = useState('tauri');
  const [deviceNameHint, setDeviceNameHint] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [pairingExpiresAt, setPairingExpiresAt] = useState('');
  const [status, setStatus] = useState<PairingStatusData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [busyStart, setBusyStart] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const [copied, setCopied] = useState(false);
  const [autoPolling, setAutoPolling] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!deviceNameHint) {
      setDeviceNameHint(`Event.Tools desktop (${typeof navigator !== 'undefined' ? navigator.platform : 'web'})`);
    }
  }, [deviceNameHint]);

  useEffect(() => {
    if (orgOptions.length === 0) {
      setOrganizationId('');
      return;
    }
    if (!organizationId || !orgOptions.some((org) => org._id === organizationId)) {
      setOrganizationId(orgOptions[0]?._id ?? '');
    }
  }, [organizationId, orgOptions]);

  useEffect(() => () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const sharedCoordinatorUrl = (path: string) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${SHARED_COORDINATOR_URL}${normalizedPath}`;
  };

  const startPairing = useCallback(async () => {
    setError(null);
    setStatus(null);
    setStatusError(null);
    setAutoPolling(false);
    setBusyStart(true);
    try {
      const body: Record<string, string> = {
        requestedClientType: clientType,
        requestedAppProduct: appProduct,
        deviceNameHint: normalizeTrimmed(deviceNameHint) || 'Event.Tools desktop',
        protocolVersion: '2026-06-13.v1',
        appVersion: '1.0.0',
        platform: typeof navigator !== 'undefined' ? (navigator.platform || 'web') : 'web',
      };
      if (isString(organizationId)) {
        body.organizationId = organizationId;
      }

      const response = await fetch(sharedCoordinatorUrl('/api/client/pairing/start'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = (await response.json().catch(() => null)) as ApiResponse<PairingStartData> | null;
      if (!response.ok || !json?.ok || !json.data) {
        throw new Error(json?.error || `Could not start pairing (${response.status}).`);
      }

      setPairingCode(json.data.pairingCode);
      setPairingExpiresAt(typeof json.data.expiresAt === 'number' ? String(json.data.expiresAt) : String(json.data.expiresAt));
      await loadPairingStatus(json.data.pairingCode);
      setAutoPolling(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start a pairing flow.');
      setPairingCode('');
      setPairingExpiresAt('');
    } finally {
      setBusyStart(false);
    }
  }, [appProduct, clientType, deviceNameHint, organizationId]);

  const loadPairingStatus = useCallback(async (candidateCode: string = pairingCode) => {
    const code = normalizeTrimmed(candidateCode);
    if (!code) {
      setStatusError('Enter or generate a pairing code first.');
      return;
    }
    setStatusError(null);
    setBusyStatus(true);
    try {
      const response = await fetch(sharedCoordinatorUrl(`/api/client/pairing/status?code=${encodeURIComponent(code)}`), {
        method: 'GET',
        cache: 'no-store',
      });
      const json = (await response.json().catch(() => null)) as ApiResponse<PairingStatusData> | null;
      if (!response.ok || !json?.ok || !json.data) {
        throw new Error(json?.error || `Pairing status check failed (${response.status}).`);
      }
      setStatus(json.data);
      if (json.data.status !== 'pending') {
        setAutoPolling(false);
      }
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Could not load pairing status.');
    } finally {
      setBusyStatus(false);
    }
  }, [pairingCode]);

  useEffect(() => {
    if (!autoPolling || !pairingCode || status?.status !== 'pending') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        void loadPairingStatus();
      }, 4500);
      void loadPairingStatus();
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [autoPolling, pairingCode, status?.status, loadPairingStatus]);

  const copyCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const isReady = convexAuth.isAuthenticated && user !== undefined && orgOptions.length > 0;
  const canStartPairing = status?.status !== 'pending' || !pairingCode;

  return (
    <div className="min-h-screen bg-[#090b0f] text-[#edf2f7] px-4 py-10">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <p className="text-xs uppercase tracking-[0.12em] text-[#62d3b5]">Shared Coordinator</p>
          <h1 className="mt-1 text-2xl font-semibold">Pairing</h1>
          <p className="mt-2 max-w-3xl text-sm text-[#9fb0c3]">
            Generate a one-time pairing code to link Event.Tools machines to this shared account.
            Use this from the desktop pairing flow (or manual claim flow) so browser library metadata
            can sync from local installs to your online dashboard.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/account" className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] hover:bg-[#273a4f]">
              Account
            </Link>
            <Link to="/library" className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] hover:bg-[#273a4f]">
              Browser library
            </Link>
            <Link to="/setup" className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] hover:bg-[#273a4f]">
              Setup guide
            </Link>
          </div>
        </header>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Sign-in &amp; account scope</h2>
          <p className="mt-1 text-sm text-[#9fb0c3]">
            Pairing is scoped to your logged-in account when available; if not signed in, a temporary
            local fallback account is used.
          </p>
          {!convexAuth.isAuthenticated || user === undefined ? (
            <p className="mt-3 text-sm text-[#9fb0c3]">
              You are not currently signed in in this browser.
              <button
                type="button"
                onClick={() => void signIn()}
                className="ml-2 inline rounded bg-[#62d3b5] px-3 py-1.5 text-sm text-[#0c1117] hover:bg-[#50bea0]"
              >
                Sign in
              </button>
            </p>
          ) : (
            <p className="mt-3 text-sm text-[#9fb0c3]">
              Signed in as <span className="font-mono text-[#62d3b5]">{user?.email ?? user?.name ?? 'user'}</span>.
            </p>
          )}
          {orgOptions.length > 0 ? (
            <label className="mt-4 block">
              <span className="text-xs text-[#9fb0c3]">Organization</span>
              <select
                value={organizationId}
                onChange={(event) => setOrganizationId(event.target.value)}
                className="mt-1 w-full max-w-xl rounded border border-[#2b3a4d] bg-[#090b0f] px-3 py-2 text-sm text-[#edf2f7]"
              >
                {orgOptions.map((org) => (
                  <option key={org._id} value={org._id}>
                    {org.name} ({org.memberCount} member{org.memberCount === 1 ? '' : 's'})
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="mt-3 text-xs text-[#9fb0c3]">
              No organization selected. A personal account fallback will be used if you create a pairing now.
            </p>
          )}
        </section>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Create a pairing code</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="text-xs text-[#9fb0c3]">Device display name</span>
              <input
                value={deviceNameHint}
                onChange={(event) => setDeviceNameHint(event.target.value)}
                className="mt-1 w-full rounded border border-[#2b3a4d] bg-[#090b0f] px-3 py-2 text-sm outline-none focus:border-[#62d3b5]"
                placeholder="Event.Tools control room"
              />
            </label>
            <label className="block">
              <span className="text-xs text-[#9fb0c3]">Client type</span>
              <select
                value={clientType}
                onChange={(event) => setClientType(event.target.value)}
                className="mt-1 w-full rounded border border-[#2b3a4d] bg-[#090b0f] px-3 py-2 text-sm text-[#edf2f7]"
              >
                <option value="tauri">tauri</option>
                <option value="electron">electron</option>
                <option value="web">web</option>
                <option value="node_service">node_service</option>
                <option value="shared_media_node">shared_media_node</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-[#9fb0c3]">App product</span>
              <select
                value={appProduct}
                onChange={(event) => setAppProduct(event.target.value)}
                className="mt-1 w-full rounded border border-[#2b3a4d] bg-[#090b0f] px-3 py-2 text-sm text-[#edf2f7]"
              >
                <option value="event_tools">event_tools</option>
                <option value="vj_tools">vj_tools</option>
                <option value="shared_media_node">shared_media_node</option>
              </select>
            </label>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => void startPairing()}
                disabled={busyStart || !canStartPairing}
                className="rounded bg-[#24384d] px-4 py-2 text-sm text-[#e4ecf4] disabled:opacity-50"
              >
                {busyStart ? 'Starting...' : 'Start pairing'}
              </button>
            </div>
          </div>
          {error ? <p className="mt-3 text-sm text-[#ff6b6b]">{error}</p> : null}
          {!isReady && convexAuth.isAuthenticated ? (
            <p className="mt-3 text-xs text-[#9fb0c3]">
              You are signed in, but still not ready to browse org data. Open /account to create an organization.
            </p>
          ) : null}
        </section>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Pairing code</h2>
          {!pairingCode ? (
            <p className="mt-2 text-sm text-[#9fb0c3]">No active pairing code yet. Start one above to begin.</p>
          ) : (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-[0.12em] text-[#7f8fa3]">Active code</p>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded border border-[#263342] bg-[#090b0f] px-3 py-2 font-mono text-2xl tracking-[0.18em] text-[#d9e2ec]">
                  {pairingCode}
                </code>
                <button
                  type="button"
                  onClick={() => void copyCode()}
                  className="rounded border border-[#2b3a4d] px-3 py-2 text-xs"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-2 text-xs text-[#9fb0c3]">Expires at {new Date(Number(pairingExpiresAt) || pairingExpiresAt).toLocaleString()}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void loadPairingStatus()}
                  disabled={busyStatus}
                  className="rounded bg-[#24384d] px-3 py-2 text-xs text-[#e4ecf4] disabled:opacity-50"
                >
                  {busyStatus ? 'Checking...' : autoPolling ? 'Checking status' : 'Check status'}
                </button>
                <button
                  type="button"
                  onClick={() => setAutoPolling((value) => !value)}
                  className="rounded border border-[#2b3a4d] px-3 py-2 text-xs"
                >
                  {autoPolling ? 'Stop polling' : 'Auto-check pairing'}
                </button>
              </div>
            </div>
          )}
          {statusError ? <p className="mt-3 text-sm text-[#ff6b6b]">{statusError}</p> : null}
        </section>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Current status</h2>
          {!status ? (
            <p className="mt-2 text-sm text-[#9fb0c3]">Create a code to see live pairing status.</p>
          ) : (
            <div className="mt-2 space-y-2 text-sm">
              <p className="text-[#d9e2ec]">Status: <span className="font-mono uppercase">{status.status}</span></p>
              <p>Org/Account: <span className="font-mono text-[#d9e2ec]">{status.organizationId}</span></p>
              <p>Target client: {status.requestedClientType ?? '—'} / {status.requestedAppProduct ?? '—'}</p>
              <p>Hint: {status.deviceNameHint ?? '—'} · {status.libraryNameHint ?? 'No library hint'}</p>
              {status.targetLibraryId ? <p>Target Library ID: <span className="font-mono">{status.targetLibraryId}</span></p> : null}
              {status.claimedDeviceId ? <p>Claimed Device: <span className="font-mono">{status.claimedDeviceId}</span></p> : null}
              {status.membershipRole || status.trustState ? <p>Role/Trust: {status.membershipRole ?? '—'} / {status.trustState ?? '—'}</p> : null}
            </div>
          )}
        </section>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Desktop completion</h2>
          <p className="mt-2 text-sm text-[#9fb0c3]">
            In Event.Tools desktop, open settings and use the shared coordinator pairing flow. It will connect with this code
            and expose metadata to this website once synced.
          </p>
          <p className="mt-3 text-sm text-[#9fb0c3]">
            If pairing succeeds, open /library from this browser to start browsing synced metadata and launch
            asset requests through an active desktop.
          </p>
        </section>
      </div>
    </div>
  );
}

export default PairPage;
